import { useEffect } from 'react';
import type { Role } from '@cusp/core';
import { devIdentitiesAllowed } from '../config.js';
import { useDevIdentities } from '../hooks/useDevIdentities.js';
import { getIdentitySource, setIdentity, type Identity } from '../identity.js';
import { useIdentity } from '../useIdentity.js';

/**
 * Keyed on (tenant, user), never on user alone — Sam Rivera holds two
 * memberships in the demo seed, so a user-keyed option list would collapse two
 * genuinely different identities into one.
 */
const key = (i: Pick<Identity, 'tenantId' | 'userId'>): string => `${i.tenantId}|${i.userId}`;

export interface DevIdentitySwitcherProps {
  /** On a cold start with no stored identity, prefer this role when selecting. */
  preferRole?: Role;
  /**
   * Offer only identities holding one of these roles. The admin console passes
   * the roles its routes actually accept — offering a learner there is offering
   * a tour of 403s, and it muddies the demo: the person clicking through IS the
   * institution admin, and the switcher should read as "which institution",
   * not "which of five random people".
   */
  roles?: readonly Role[];
}

/**
 * Dev-only identity switcher — OPT-IN, and renders nothing by default.
 *
 * The seed's ids are database-generated, so the header triple can't be
 * hardcoded — the option list comes from GET /v1/dev/identities. Selecting
 * writes to localStorage; every request thereafter carries the triple, and
 * every identity-scoped query key repartitions.
 *
 * The gate lives in this wrapper rather than inside the picker so that when it
 * is off, the `/v1/dev/identities` request is never issued at all — a `return
 * null` after the hooks would still have fetched the identity list. Enable with
 * `configureApiClient({ devIdentities: true })`; see config.ts for why this is
 * default-off.
 */
export function DevIdentitySwitcher(props: DevIdentitySwitcherProps) {
  if (!devIdentitiesAllowed()) return null;
  return <IdentityPicker {...props} />;
}

function IdentityPicker({ preferRole, roles }: DevIdentitySwitcherProps) {
  const current = useIdentity();
  const { data, isPending, error } = useDevIdentities();
  const all = data?.identities ?? [];
  const identities = roles ? all.filter((i) => roles.includes(i.role)) : all;

  // `pnpm test` truncates and reseeds the database (see CLAUDE.md), which makes
  // every stored id a ghost that produces 400s. Drop a stored identity that is
  // no longer in the list rather than leaving the app wedged on errors — this
  // WILL happen to anyone who runs the test suite with a browser tab open.
  useEffect(() => {
    if (identities.length === 0) return;
    if (current !== null) {
      // Refresh the membership's labels too: retaining only its id leaves a
      // renamed school under its old name in every screen using the store.
      // setIdentity suppresses notifications when the fields are unchanged.
      setIdentity(
        identities.find((i) => key(i) === key(current)) ?? null,
        getIdentitySource() ?? 'dev',
      );
      return;
    }
    if (current === null) {
      const pick = (preferRole && identities.find((i) => i.role === preferRole)) ?? identities[0];
      if (pick) setIdentity(pick, 'dev');
    }
  }, [identities, current, preferRole]);

  if (isPending) return <span>loading identities…</span>;
  if (error) return <span role="alert">dev identities unavailable — {error.message}</span>;

  return (
    <label className="identity-switcher">
      Acting as{' '}
      <select
        value={current ? key(current) : ''}
        onChange={(e) =>
          setIdentity(identities.find((i) => key(i) === e.target.value) ?? null, 'dev')
        }
      >
        <option value="">— none —</option>
        {identities.map((i) => (
          <option key={key(i)} value={key(i)}>
            {i.userName} @ {i.tenantName} ({i.role})
          </option>
        ))}
      </select>
    </label>
  );
}
